import { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BlockedScoreTooltip } from 'src/components/BlockedScoreTooltip';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { Score, SCORE_SIZE } from 'src/components/Score';
import { TextLink } from 'src/components/TextLink';
import { isNonEnglish, LANGUAGE } from 'src/data/generation';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { calculateBlockedReason } from 'src/data/utils';
import { HIDE_NON_ENGLISH_SCORE_CUTOFF_DATE } from 'src/data/variation';

import { useStyles } from './useStyles';

export const DocumentScorePanel = ({
  className,
  instanceId,
  score,
  generationTool,
  onDetailsClick,
  onHover,
  expanded,
  loading,
  text,
  language,
  disabled,
  isLastBlockScore,
  variation,
}) => {
  const styles = useStyles();
  const [hovered, setHovered] = useState(false);
  const localExpanded = expanded || hovered;

  const handleScoreMouseEnter = useCallback(() => {
    setHovered(true);
    onHover(instanceId);
  }, [onHover, instanceId]);

  const handleRootMouseLeave = useCallback(() => {
    setHovered(false);
    onHover(null);
  }, [onHover]);

  const handleDetailsClick = useCallback(() => {
    onDetailsClick(instanceId);
  }, [onDetailsClick, instanceId]);

  const blockedReason = useMemo(() => calculateBlockedReason(text), [text]);

  const generationToolIcon = generationTool && (
    <GenerationToolIcon
      className={styles.icon}
      tool={generationTool}
    />
  );

  const showScore = !(
    score === 0 &&
    isNonEnglish(language) &&
    !blockedReason &&
    (!variation.lastUpdateTime || variation.lastUpdateTime < HIDE_NON_ENGLISH_SCORE_CUTOFF_DATE)
  );

  return (
    <div
      className={cx(styles.root, className, {
        [styles.expanded]: localExpanded,
        [styles.disabled]: disabled,
      })}
      onMouseLeave={handleRootMouseLeave}
      onClick={disabled ? null : handleDetailsClick}
    >
      {showScore && (
        <BlockedScoreTooltip blockedReason={blockedReason}>
          <Score
            className={styles.score}
            score={score}
            locked={Boolean(blockedReason)}
            blockedReason={blockedReason}
            size={SCORE_SIZE.large}
            loading={loading}
            onMouseEnter={handleScoreMouseEnter}
            isTargetForHint={isLastBlockScore}
            generationToolIcon={generationToolIcon}
          />
        </BlockedScoreTooltip>
      )}

      <div className={styles.panel}>
        <TextLink className={styles.link}>
          Insights <span className={styles.arrow}>&rsaquo;</span>
        </TextLink>
      </div>
    </div>
  );
};

DocumentScorePanel.propTypes = {
  className: PropTypes.string,
  instanceId: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  onDetailsClick: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)),
  expanded: PropTypes.bool,
  loading: PropTypes.bool,
  text: PropTypes.string,
  language: PropTypes.oneOf(Object.values(LANGUAGE)),
  disabled: PropTypes.bool,
  isLastBlockScore: PropTypes.bool,
  variation: PropTypes.shape({
    lastUpdateTime: PropTypes.string,
  }),
};

DocumentScorePanel.defaultProps = {
  className: null,
  expanded: false,
  loading: false,
  text: null,
  generationTool: null,
  language: null,
  disabled: false,
  isLastBlockScore: false,
  variation: {
    lastUpdateTime: HIDE_NON_ENGLISH_SCORE_CUTOFF_DATE,
  },
};
